import { BaseEntity } from './base-raptor-models';
import { Customer } from './customer.model';
import { InventoryList } from './inventory-list.model';
import { Part } from './part.model';
import { PurchaseOrder } from './purchase-order.model';
import { ReceivingDocument } from './receiving-document.model';
import { CountingDocument, CountingDocumentItem } from './counting-document.model';
import { WorkOrder } from './work-order.model';

export enum InventoryCountingType {
    WorkOrder = 'WorkOrder',
    ReceivingDocument = 'ReceivingDocument',
    PurchaseOrder = 'PurchaseOrder',
    InventoryList = 'InventoryList',
}

export interface InventoryCountingEntity extends BaseEntity {
    number: string;
    date: Date;
    customerId: string;
    customer?: Customer;
    sections: InventoryCountingEntitySection[];
    total: number;
    statusId: string;
    status?: InventoryCountingEntityStatus;
    type?: InventoryCountingType;
    completedDate?: Date;
    countingDocumentId?: string;
    countingDocument?: CountingDocument;
    name?: string;
    referenceNumber?: string;
}

export interface InventoryCountingEntitySection extends BaseEntity {
    countingEntityId: string;
    countingEntity?: InventoryCountingEntity;
    name: string;
    items: InventoryCountingEntityItem[];
}

export interface InventoryCountingEntityItem extends BaseEntity {
    countingEntitySectionId: string;
    countingEntitySection?: InventoryCountingEntitySection;
    partId?: string;
    part?: Part;
    partAmount?: number;
    partQuantity?: number;
    position: number;
    countingDocumentItemId?: string;
    countingDocumentItem?: CountingDocumentItem;
}

export interface InventoryCountingEntityStatus extends BaseEntity {
    name: string;
    color?: string;
    order?: number;
}

export function convertFromWorkOrder(entity: WorkOrder): InventoryCountingEntity {
    return {
        id: entity.id,
        branchId: entity.branchId,
        tenantId: entity.tenantId,
        type: InventoryCountingType.WorkOrder,
        number: entity.number,
        customerId: entity.customerId,
        customer: entity.customer,
        date: entity.date,
        total: entity.total,
        statusId: entity.statusId ?? '',
        status: {
            id: entity.status?.id ?? '',
            branchId: entity.status?.branchId ?? '',
            tenantId: entity.status?.tenantId ?? '',
            name: entity.status?.name ?? '',
            color: entity.status?.color ?? '',
            order: entity.status?.order ?? 0,
        },
        completedDate: entity.completedDate,
        sections:
            entity.sections?.map((section) => ({
                id: section.id,
                countingEntityId: entity?.id ?? '',
                name: section.name,
                branchId: section.branchId,
                tenantId: section.tenantId,
                items:
                    section.items?.flatMap((item) =>
                        item.lines
                            ?.filter((line) => line.partId)
                            .map((line) => ({
                                id: line.id,
                                branchId: line.branchId,
                                tenantId: line.tenantId,
                                countingEntitySectionId: section.id ?? '',
                                partId: line.partId,
                                part: line.part,
                                partAmount: line.partAmount,
                                partQuantity: line.partQuantity,
                                position: line.order,
                                countingDocumentItemId: line.countingDocumentItemId,
                                countingDocumentItem: line.countingDocumentItem,
                            })) ?? []
                    ) ?? [],
            })) ?? [],
        countingDocumentId: entity.countingDocumentId,
        countingDocument: entity.countingDocument,
    };
}

export function convertFromRDPO(entity: ReceivingDocument | PurchaseOrder): InventoryCountingEntity {
    return {
        id: entity.id,
        type: (entity as PurchaseOrder).requesterId ? InventoryCountingType.PurchaseOrder : InventoryCountingType.ReceivingDocument,
        number: entity.number,
        referenceNumber: entity.referenceNumber,
        customerId: entity.customerId,
        customer: entity.customer,
        date: entity.date,
        total: entity.total,
        statusId: entity.statusId,
        status: {
            id: entity.status?.id ?? '',
            name: entity.status?.name ?? '',
            branchId: entity.status?.branchId ?? '',
            tenantId: entity.status?.tenantId ?? '',
        },
        completedDate: entity.completedDate,
        sections:
            entity.sections?.map((section) => ({
                id: section.id,
                countingEntityId: entity?.id ?? '',
                name: section.name,
                branchId: section.branchId,
                tenantId: section.tenantId,
                items:
                    section.items?.map((item) => ({
                        id: item.id,
                        countingEntitySectionId: section.id ?? '',
                        partId: item.partId ?? '',
                        part: item.part,
                        partAmount: item.amount,
                        partQuantity: item.quantity,
                        position: item.position,
                        countingDocumentItemId: item.countingDocumentItemId,
                        countingDocumentItem: item.countingDocumentItem,
                        branchId: item.branchId,
                        tenantId: item.tenantId,
                    })) ?? [],
            })) ?? [],
        countingDocumentId: entity.countingDocumentId,
        countingDocument: entity.countingDocument,
        branchId: entity.branchId,
        tenantId: entity.tenantId,
    };
}

export function convertFromInventoryList(entity: InventoryList): InventoryCountingEntity {
    return {
        id: entity.id,
        type: InventoryCountingType.InventoryList,
        name: entity.name,
        date: entity.createdDate ? entity.createdDate : new Date(),
        branchId: entity.branchId,
        tenantId: entity.tenantId,
        number: '',
        customerId: '',
        total: 0,
        statusId: '',
        sections: [
            {
                id: entity.id,
                branchId: entity.branchId,
                tenantId: entity.tenantId,
                name: entity.name,
                countingEntityId: entity.id ?? '',
                items:
                    entity.items?.map((item) => ({
                        id: item.id,
                        branchId: item.branchId,
                        tenantId: item.tenantId,
                        partId: item.inventory?.partId,
                        part: item.inventory?.part,
                        partQuantity: item.inventory?.quantity,
                        countingEntitySectionId: entity.id ?? '',
                        countingDocumentItemId: item.countingDocumentItemId,
                        countingDocumentItem: item.countingDocumentItem,
                        position: 0,
                    })) ?? [],
            },
        ],
        countingDocumentId: entity.countingDocumentId,
        countingDocument: entity.countingDocument,
    };
}
